// ═══════════════════════════════════════════════════════════════
// ZoneMapOverlay — Reusable component to render GeoOps zones
// on any Leaflet map. Import into Impact, Debris, Shelter, etc.
// ═══════════════════════════════════════════════════════════════

import { Polygon, Polyline, Circle, Popup } from 'react-leaflet';
import { Text, Badge, Group, Stack } from '@mantine/core';
import type { GeoOpsZone } from '../../store/geoopsStore';
import { ZONE_TYPE_META } from '../../store/geoopsStore';

interface ZoneMapOverlayProps {
  zones: GeoOpsZone[];
  interactive?: boolean;
  onZoneClick?: (zone: GeoOpsZone) => void;
}

export default function ZoneMapOverlay({ zones, interactive = true, onZoneClick }: ZoneMapOverlayProps) {
  return (
    <>
      {zones.map(zone => {
        if (!zone.geometry) return null;
        const meta = ZONE_TYPE_META[zone.zoneType] || ZONE_TYPE_META.custom;
        const color = zone.color || meta.color;
        const opacity = zone.opacity ?? 0.3;

        const popup = interactive ? (
          <Popup>
            <Stack gap={4}>
              <Text size="sm" fw={600}>{zone.name}</Text>
              <Group gap={4}>
                <Badge size="xs" color={meta.mantine}>{meta.label}</Badge>
                <Badge size="xs" variant="dot"
                  color={zone.status === 'active' ? 'green' : zone.status === 'planned' ? 'yellow' : 'gray'}>
                  {zone.status}
                </Badge>
              </Group>
              {zone.notes && <Text size="xs" c="dimmed" lineClamp={2}>{zone.notes}</Text>}
              {zone.lastUpdated && (
                <Text size="xs" c="dimmed">Updated: {new Date(zone.lastUpdated).toLocaleString()}</Text>
              )}
            </Stack>
          </Popup>
        ) : null;

        if (zone.geometry.type === 'Polygon') {
          const positions = zone.geometry.coordinates[0].map(
            ([lng, lat]) => [lat, lng] as [number, number]
          );
          return (
            <Polygon key={zone.id} positions={positions}
              pathOptions={{ color, fillColor: color, fillOpacity: opacity, weight: 2 }}
              eventHandlers={onZoneClick ? { click: () => onZoneClick(zone) } : {}}>
              {popup}
            </Polygon>
          );
        }

        if (zone.geometry.type === 'LineString') {
          const positions = zone.geometry.coordinates.map(
            ([lng, lat]) => [lat, lng] as [number, number]
          );
          return (
            <Polyline key={zone.id} positions={positions}
              pathOptions={{ color, weight: 4, opacity: 0.8, dashArray: '10 5' }}
              eventHandlers={onZoneClick ? { click: () => onZoneClick(zone) } : {}}>
              {popup}
            </Polyline>
          );
        }

        if (zone.geometry.type === 'Point') {
          const [lng, lat] = zone.geometry.coordinates;
          const radius = (zone.metadata?.radiusMeters as number) || 500;
          return (
            <Circle key={zone.id} center={[lat, lng]} radius={radius}
              pathOptions={{ color, fillColor: color, fillOpacity: opacity, weight: 2 }}
              eventHandlers={onZoneClick ? { click: () => onZoneClick(zone) } : {}}>
              {popup}
            </Circle>
          );
        }

        return null;
      })}
    </>
  );
}
